/**
 * PWA lifecycle — service-worker registration + update/offline state.
 *
 * registerType is 'prompt' (see vite.config.ts): a new build does NOT activate
 * itself. `onNeedRefresh` flips `needRefresh`, the UpdatePrompt shows a Reload
 * bar, and `reload()` activates the waiting worker + reloads — so a rep is never
 * force-refreshed mid-order. `onOfflineReady` confirms the app is cached for
 * offline use.
 */
import { create } from 'zustand';

interface PwaState {
  needRefresh: boolean;
  offlineReady: boolean;
  /** Activate the waiting service worker and reload. Set after registration. */
  reload: () => void;
  dismiss: () => void;
  clearOfflineReady: () => void;
}

export const usePwa = create<PwaState>(() => ({
  needRefresh: false,
  offlineReady: false,
  reload: () => {},
  dismiss: () => usePwa.setState({ needRefresh: false }),
  clearOfflineReady: () => usePwa.setState({ offlineReady: false }),
}));

/** Register the service worker and wire its update/offline callbacks. */
export async function initPwa(): Promise<void> {
  if (typeof window === 'undefined' || !('serviceWorker' in navigator)) return;
  try {
    const { registerSW } = await import('virtual:pwa-register');
    const updateSW = registerSW({
      onNeedRefresh() {
        usePwa.setState({ needRefresh: true });
      },
      onOfflineReady() {
        usePwa.setState({ offlineReady: true });
      },
    });
    usePwa.setState({ reload: () => void updateSW(true) });
  } catch {
    // No SW in dev unless devOptions.enabled — safe to ignore.
  }
}
